// built-from: /contextual-facilitator-companion v2 impl:contextual-facilitator-companion role:implementation
// Spec 100 slice 1: ONE activity shell. It owns the activity title and context
// line, the save feedback slot, the chat launcher, the dock's open / collapse /
// expand / close controls and the responsive layout. The activity supplies its
// working content, optional actions and an optional conversation binding
// (attachFacilitator + ContextualFacilitatorCompanion). There is no second dock
// and no second chat: the shell drives the shared FacilitatorDock through its
// control seam.
//
// Closing or collapsing is PRESENTATION ONLY. The bound conversation stays
// mounted and hidden, so an unsent draft survives, no session is sealed and no
// replacement session is created.
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  FacilitatorDockControlProvider,
  type FacilitatorDockControl,
} from '../components/FacilitatorDock';
import '../styles/ActivityShell.css';

/** The six states this shell specifies once for every activity. */
export type ActivityChatState =
  | 'closed'
  | 'opening'
  | 'open'
  | 'collapsed'
  | 'expanded'
  | 'failure';

/** What the shell remembers per browser. */
type ChatPresentation = 'closed' | 'open' | 'collapsed' | 'expanded';

export interface ActivityShellConversation {
  /** Renders the bound conversation, normally <ContextualFacilitatorCompanion />. */
  render: (controls: { close: () => void }) => ReactNode;
  /** True while the activity is still opening the conversation. */
  opening?: boolean;
  /** Set when the conversation could not be opened. */
  error?: string | null;
  /** Offered after a failure. */
  onRetry?: () => void;
}

export interface ActivityShellProps {
  title: string;
  /** The one-line context under the title (engagement, activity, method). */
  context?: string;
  /** The save feedback slot; the default reads Saving... / Saved. */
  saving?: boolean;
  saveFeedback?: ReactNode;
  /** A completed (read-only) session carries a text tag, never colour alone. */
  sealed?: boolean;
  /** Activity-supplied actions, placed before the shared chat controls. */
  actions?: ReactNode;
  /** Omit for an activity with no conversation: no dead chat controls appear. */
  conversation?: ActivityShellConversation;
  /** A changed value opens the existing conversation and focuses its composer. */
  conversationOpenRequest?: number;
  /** Per-browser memory of the chosen presentation. */
  rememberKey?: string;
  /** An activity embedded inside another screen that already carries the page
   * title (Event Storming inside the Business map). Same shell, same states,
   * same one dock: only the shell's own header steps aside so the page does
   * not show two titles. */
  embedded?: boolean;
  children: ReactNode;
}

const DEFAULT_REMEMBER_KEY = 'ap-activity-chat';
const NARROW_QUERY = '(max-width: 760px)';
const PRESENTATIONS: ChatPresentation[] = ['closed', 'open', 'collapsed', 'expanded'];

// Storage faults degrade to the viewport default; they never break the screen.
function readRemembered(key: string): ChatPresentation | null {
  try {
    const stored = window.localStorage.getItem(key);
    return PRESENTATIONS.includes(stored as ChatPresentation) ? stored as ChatPresentation : null;
  } catch {
    return null;
  }
}

function writeRemembered(key: string, value: ChatPresentation): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Keep the choice in memory for this visit.
  }
}

/** The same breakpoint the application shell uses, so the two agree. */
function useNarrowViewport(): boolean {
  const [narrow, setNarrow] = useState(() => window.matchMedia?.(NARROW_QUERY).matches || false);
  useEffect(() => {
    const media = window.matchMedia?.(NARROW_QUERY);
    if (!media) return;
    const update = () => setNarrow(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);
  return narrow;
}

export function ActivityShell({
  title,
  context,
  saving = false,
  saveFeedback,
  sealed = false,
  actions,
  conversation,
  conversationOpenRequest = 0,
  rememberKey = DEFAULT_REMEMBER_KEY,
  embedded = false,
  children,
}: ActivityShellProps) {
  const dockId = useId();
  const launcherRef = useRef<HTMLButtonElement>(null);
  const shellRef = useRef<HTMLElement>(null);
  const previousOpenRequest = useRef(conversationOpenRequest);
  const narrow = useNarrowViewport();
  // Nothing remembered yet: open on desktop, collapsed on a narrow viewport.
  const [chosen, setChosen] = useState<ChatPresentation | null>(() => readRemembered(rememberKey));
  const [returningFocus, setReturningFocus] = useState(false);
  const presentation: ChatPresentation = chosen ?? (narrow ? 'collapsed' : 'open');

  const choose = useCallback((next: ChatPresentation) => {
    setChosen(next);
    writeRemembered(rememberKey, next);
  }, [rememberKey]);

  const openChat = useCallback(() => choose('open'), [choose]);
  useEffect(() => {
    if (previousOpenRequest.current === conversationOpenRequest) return;
    previousOpenRequest.current = conversationOpenRequest;
    choose('open');
    window.setTimeout(() => shellRef.current?.querySelector<HTMLTextAreaElement>('textarea[aria-label="Ask or add context"]')?.focus(), 0);
  }, [choose, conversationOpenRequest]);
  const closeChat = useCallback(() => {
    choose('closed');
    setReturningFocus(true);
  }, [choose]);

  const state: ActivityChatState = !conversation
    ? 'closed'
    : presentation === 'closed'
      ? 'closed'
      : conversation.error
        ? 'failure'
        : conversation.opening
          ? 'opening'
          : presentation;

  // Closing returns focus to the launcher that reopens the conversation.
  useEffect(() => {
    if (!returningFocus || state !== 'closed') return;
    launcherRef.current?.focus();
    setReturningFocus(false);
  }, [returningFocus, state]);

  const control: FacilitatorDockControl = {
    collapsed: state === 'collapsed',
    expanded: state === 'expanded',
    setCollapsed: (collapsed) => choose(collapsed ? 'collapsed' : 'open'),
    setExpanded: (expanded) => choose(expanded ? 'expanded' : 'open'),
    close: closeChat,
  };

  const showLauncher = Boolean(conversation) && state === 'closed';

  return (
    // Deliberately unnamed: the activity's own <h1> names the screen. A
    // second accessible name here would answer to the same words as the
    // working surface it contains (the process landscape).
    <section ref={shellRef} className="activity-shell fshell" data-chat-state={state}>
      <header className="fshell-header activity-shell__header">
        <div hidden={embedded}>
          <h1 className="fshell-title">{title}</h1>
          {context ? <p className="fshell-context">{context}</p> : null}
        </div>
        <span className="fshell-spacer" />
        {actions ? <div className="activity-shell__actions">{actions}</div> : null}
        <span className="fshell-save">{saveFeedback ?? (saving ? 'Saving...' : 'Saved')}</span>
        {sealed ? <span className="fshell-closed">Closed</span> : null}
        {showLauncher ? (
          <button
            ref={launcherRef}
            type="button"
            className="fshell-close activity-shell__launcher"
            aria-expanded={false}
            aria-controls={dockId}
            onClick={openChat}
          >
            Open facilitator
          </button>
        ) : null}
      </header>
      <div className="fshell-body activity-shell__body">
        <main className="fshell-surface activity-shell__work">{children}</main>
        {conversation ? (
          <div className="activity-shell__dock" id={dockId} hidden={state === 'closed'}>
            {state === 'opening' ? (
              <p className="activity-shell__status" role="status">Opening the facilitator...</p>
            ) : null}
            {state === 'failure' ? (
              <div className="fshell-alert activity-shell__failure" role="alert">
                <p>{conversation.error}</p>
                {conversation.onRetry ? (
                  <button type="button" className="fshell-close" onClick={conversation.onRetry}>Try again</button>
                ) : null}
                <button type="button" className="fshell-close" onClick={closeChat}>Close facilitator</button>
              </div>
            ) : null}
            {/* Kept mounted through every state: the draft, the transcript and
                the session outlive any presentation change. */}
            <div className="activity-shell__conversation" hidden={state === 'failure'}>
              <FacilitatorDockControlProvider value={control}>
                {conversation.render({ close: closeChat })}
              </FacilitatorDockControlProvider>
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
}

export default ActivityShell;
